import React, { useState, useEffect, createRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import "./signinwithpin.scss";
import Button from "components/Button/index";
import { useToast } from "components/Toaster/index";
import { signInWithToken, Organization, StoragePlan } from "@induz/sdk";
import { getUserTypeFromUser, getUserTypeSessionValue } from "utils/helperFunction";
import { signInContext } from "context/SigninContext";

const SignInWithPin = ({
  title = "Enter Pin ",
  description = "Pin and token based signin",
  btnText = "SignIn",
  ids,
  setStartWithPin,
}) => {
  const [count] = useState(6);
  const [pin, setPin] = useState("");
  const [showError, setShowError] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const navigate = useNavigate();
  const showToast = useToast();
  const { setIsTypeSelect, setUser, setOrgnization, setUserMail } = useContext(signInContext);

  const [refs] = useState(
    Array(count)
      .fill(1)
      .map(() => createRef())
  );

  // Update the pin state
  const updatePinState = () => {
    const otpValue = refs.map((ref) => ref.current.value || "").join("");
    setPin(otpValue);

    if (otpValue.length === 0) {
      setShowError(false);
    }
  };

  // Handle digit input change (replace digit logic)

  const isDigit = (k) => /^[0-9]$/.test(k);
  const handleChange = (val, ref, idx) => {

    const digit = val.replace(/\D/g, "").slice(-1); // Get only the last digit, remove non-numeric

    if (!digit) {
      ref.current.value = "";
      return;
    }

    ref.current.value = digit;

    const updatedPin = [...pin];
    updatedPin[idx] = val;



    setPin(updatedPin.join("")); // Ensures all characters are stored properly

    if (val && idx < count - 1) {
      refs[idx + 1].current.focus();
    } else if (!val && idx > 0) {
      refs[idx - 1].current.focus();
    }

    if (showError) {
      setShowError(false); // Remove error when user starts typing again
    }
  };
  // Handle keydown events for backspace and arrow keys
  const handleKeyDown = (e, ref, idx) => {
    // console.log(e.key, "key pressed");


    if (isDigit(e.key)) {
      e.preventDefault();
      ref.current.value = e.key;


      const otpArray = refs.map((r) => r.current.value || "");
      otpArray[idx] = e.key;
      setPin(otpArray.join(""));

      if (idx < count - 1) {
        refs[idx + 1].current.focus();
      }
      return;
    }




    const input = ref.current;

    if (e.key === "Backspace") {
      if (input.value === "") {
        if (idx > 0) {
          const prevInput = refs[idx - 1].current;
          prevInput.focus();
          setCursorToEnd(prevInput);
          prevInput.value = ""; // Clear previous input
          updatePinState();
        }
      } else {
        input.value = "";
        setPin((prev) => {
          const updated = [...prev];
          updated[idx] = "";
          return updated.join("");
        });
        updatePinState();
      }
      e.preventDefault(); // Prevent default backspace behavior
    }

    if (e.key === "ArrowLeft") {
      if (idx > 0) {
        const prevInput = refs[idx - 1].current;
        prevInput.focus();
        setCursorToEnd(prevInput);
      }
      e.preventDefault(); // Prevent default arrow left behavior
    }

    if (e.key === "ArrowRight" && idx < count - 1) {
      const nextInput = refs[idx + 1].current;
      nextInput.focus();
      setCursorToEnd(nextInput);
      e.preventDefault(); // Prevent default arrow right behavior
    }

    if (e.key === "Enter") {
      const isPinComplete = pin.split("").every((digit) => digit !== "");
      if (isPinComplete) {
        addPin1();
      }
    }

  };

  // Ensure the cursor is always at the end of the input
  const setCursorToEnd = (input) => {
    requestAnimationFrame(() => {
      input.setSelectionRange(input.value.length, input.value.length);
    });
  };

  // Handle click event for focusing input
  const handleClick = (idx) => {
    const input = refs[idx].current;
    if (input) {
      input.focus();
      setCursorToEnd(input);
    }
  };

  useEffect(() => {
    refs[0].current.focus(); // Focus on the first input initially
  }, [refs]);

  const [loading, setLoading] = useState(false);

  // Handle pin submission logic

  const addPin1 = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("izauthtoken");
      const token1 = Uint8Array.from(atob(token), (c) => c.charCodeAt(0));

      const user = await signInWithToken(token1, pin);
      // console.log("User signed in successfully:", user, typeof user);

      sessionStorage.setItem("mail", user.getEmail());
      const userMail = user.getEmail();
      setUserMail(userMail);
      setUser(user);

      // Organisation instance = organisation admin; User instance: userType "User" = individual, "Employee" = organisation user
      sessionStorage.setItem("usertype", getUserTypeSessionValue(user, Organization));
      const userType = getUserTypeFromUser(user, Organization);
      if (userType === "organization") {
        setOrgnization(user);
        setIsTypeSelect("org");
      } else {
        setIsTypeSelect("individual");
      }
      
      // Store plan name AND planId in sessionStorage after PIN signin
      try {
        const userPlanId = user.getPlan && typeof user.getPlan === "function" ? user.getPlan() : null;
        if (userPlanId !== null && userPlanId !== undefined && userPlanId >= 0) {
          const storagePlan = new StoragePlan();
          const plansList = await storagePlan.listPlans();
          const userPlan = plansList.find(
            (p) => p.getPlanId && p.getPlanId() === userPlanId
          );
          if (userPlan) {
            const planName = userPlan.getname();
            sessionStorage.setItem("plan", planName);
            sessionStorage.setItem("planId", userPlanId.toString());
            console.log("Plan stored after PIN signin:", planName, "planId:", userPlanId);
          }
        }
      } catch (planError) {
        console.error("Error storing plan after PIN signin:", planError);
        // Don't block signin if plan storage fails
      }
      
      sessionStorage.removeItem("ysentinal-history"); // Clear log history on PIN sign-in
      navigate("/homepage");
    } catch (error) {
      setLoading(false);

      showToast("Invalid pin/token", "error");

      setFailedAttempts((prev) => prev + 1);

      if (failedAttempts + 1 >= 1) {

        localStorage.removeItem("izauthtoken");
        setStartWithPin(false);
        navigate("/signin");
      }
    }
  };

  const handleSignInWithPassword = () => {
    localStorage.removeItem("izauthtoken");
    setStartWithPin(false);
    navigate("/signin");
  };

  return (
    <section className="addpin">
      <div className="container addpinContainer">
        <div className="addpinBox">
          <div className="commonHeading">
            <h1>{title}</h1>
            <p>{description}</p>
          </div>
          <div className="addpinCodeBox">
            <div className="pinContainer">
              <div className="inputsWrapper" id={ids}>
                {refs.map((ref, idx) => (
                  <input
                    ref={ref}
                    style={
                      {
                        border:
                          showError && "1px solid var(--accent-colors-red)",
                      }
                    }
                    maxLength={1}
                    key={`input-${idx + 1}`}
                    id={`input-${idx + 1}`}
                    name={`input-${idx + 1}`}
                    onChange={(e) => handleChange(e.target.value, ref, idx)}
                    onKeyDown={(e) => handleKeyDown(e, ref, idx)}
                    onClick={() => handleClick(idx)}
                    inputMode="numeric"
                    type="password"
                    autoComplete="off"
                    placeholder=""
                  />
                ))}
              </div>
              <span
                style={{ visibility: showError ? "visible" : "hidden" }}
                className="errorText"
              >
                Please enter the valid pin
              </span>
            </div>
            <Button
              loading={loading}
              title={btnText}
              type="violet"
              onClick={addPin1}
              disabled={pin.length < 6}
            />
            <Button
              title="Sign In with Password"
              type="violet"
              onClick={handleSignInWithPassword}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default SignInWithPin;
